import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, ChevronDown, Clock, LogIn, LogOut, MapPin, MapPinOff, PartyPopper, Plane, Sunset } from 'lucide-react';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { EmptyState } from '../../../components/EmptyState';
import { useCheckInOut } from '../../../components/attendance/CheckInOut';
import { useAttendanceStore, type AttentionItem } from '../../../store/attendanceStore';
import { AdjustmentDialog, type AdjustmentPreset } from './AdjustmentDialog';
import {
    LocationStatusBadge, SessionStatusBadge, fmtDate, fmtMinutes, fmtTime, useElapsedMinutes,
} from './attendanceUi';

/**
 * Employee "Today": one button, the current state, today's sessions and
 * anything that needs attention. All rules are enforced by the server; this
 * screen only explains the result.
 */
export function TodayPanel({ onShowAdjustments }: { onShowAdjustments: () => void }) {
    const { today, todayLoading, fetchToday } = useAttendanceStore();
    const { requestToggle, confirmDialog } = useCheckInOut();
    const [adjust, setAdjust] = useState<AdjustmentPreset | null>(null);
    const [attentionOpen, setAttentionOpen] = useState(false);

    useEffect(() => { fetchToday().catch((e) => toast.error(e.message)); }, [fetchToday]);

    const active = today?.activeSession ?? null;
    const openMinutes = useElapsedMinutes(active?.checkIn);
    const doneMinutes = (today?.sessions || []).reduce((n, s) => n + (s.status !== 'OPEN' && s.status !== 'SUPERSEDED' ? s.creditedMinutes || 0 : 0), 0);
    const workedToday = doneMinutes + (active ? openMinutes : 0);

    if (!today) {
        return todayLoading
            ? <Card className="p-6 text-sm text-muted-foreground">Loading today's attendance…</Card>
            : <Card><EmptyState className="m-4 border-0" title="Attendance is not available" description="Your account may not be linked to an employee profile. Please contact HR." /></Card>;
    }

    const day = today.day;
    const dayNote = day.type === 'HOLIDAY' ? { icon: <PartyPopper />, text: `Holiday — ${day.holidayName}` }
        : day.type === 'LEAVE' ? { icon: <Plane />, text: `On leave — ${day.leaveType}` }
        : day.type === 'WEEK_OFF' ? { icon: <Sunset />, text: 'Week off' }
        : day.shiftName ? { icon: <Clock />, text: `${day.shiftName} shift · ${fmtTime(day.expectedStart)} – ${fmtTime(day.expectedEnd)}` }
        : null;

    return (
        <div className="space-y-4">
            {today.needsAttention.length > 0 && (
                <Card className="border-warning/40">
                    <button type="button" onClick={() => setAttentionOpen((o) => !o)}
                        aria-expanded={attentionOpen}
                        className={`flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-secondary/50 ${attentionOpen ? 'border-b border-border' : ''}`}>
                        <AlertTriangle className="size-4 text-warning-strong" />
                        <h2 className="font-display text-card-title text-foreground">Attendance requires attention</h2>
                        <Badge variant="warning">{today.needsAttention.length}</Badge>
                        <ChevronDown className={`ml-auto size-4 text-muted-foreground transition-transform ${attentionOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {attentionOpen && (
                    <ul className="divide-y divide-border">
                        {today.needsAttention.map((a: AttentionItem) => (
                            <li key={a.sessionId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-foreground">{fmtDate(a.date, { weekday: 'long', day: 'numeric', month: 'short' })}</p>
                                    <p className="text-xs text-muted-foreground">{a.reason || 'Please check this session.'}</p>
                                </div>
                                <Button size="sm" variant="secondary"
                                    onClick={() => setAdjust({ date: a.date, sessionId: a.sessionId, type: a.status === 'MISSING_CHECKOUT' ? 'MISSED_CHECK_OUT' : 'LOCATION_CORRECTION' })}>
                                    Request adjustment
                                </Button>
                            </li>
                        ))}
                    </ul>
                    )}
                </Card>
            )}

            <Card className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="space-y-3">
                        <div>
                            <p className="text-xs text-muted-foreground">{fmtDate(today.date, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                            <h2 className="font-display text-section-title text-foreground">
                                {active ? 'Checked in' : today.state === 'CHECKED_OUT' ? 'Checked out' : 'Not checked in'}
                            </h2>
                        </div>
                        {dayNote && (
                            <p className="flex items-center gap-2 text-sm text-muted-foreground [&_svg]:size-4">{dayNote.icon}{dayNote.text}</p>
                        )}
                        {active && (
                            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-3">
                                <div><dt className="text-xs text-muted-foreground">Check-in</dt><dd className="font-medium text-foreground">{fmtTime(active.checkIn)}</dd></div>
                                <div>
                                    <dt className="text-xs text-muted-foreground">Location</dt>
                                    <dd className="flex items-center gap-1 font-medium text-foreground">
                                        {active.checkInLocationStatus === 'VALID' ? <MapPin className="size-3.5 text-muted-foreground" /> : <MapPinOff className="size-3.5 text-muted-foreground" />}
                                        {active.id === 'pending' ? 'Locating…' : active.checkInLocationStatus === 'VALID' ? active.checkInLocation : 'Not verified'}
                                    </dd>
                                </div>
                                <div><dt className="text-xs text-muted-foreground">This session</dt><dd className="font-medium tabular-nums text-foreground">{fmtMinutes(openMinutes)}</dd></div>
                            </dl>
                        )}
                        {active && active.checkInLocationStatus && (
                            <div className="flex flex-wrap items-center gap-2">
                                <LocationStatusBadge status={active.checkInLocationStatus} workMode={active.workMode} />
                                {active.checkInLocation && <span className="text-xs text-muted-foreground">{active.checkInLocation}</span>}
                                {active.exception && <span className="text-xs text-muted-foreground">{active.exception}</span>}</div>
                        )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                        <p className="text-xs text-muted-foreground">Worked today</p>
                        <p className="font-display text-metric tabular-nums text-foreground">{fmtMinutes(workedToday)}</p>
                        {active ? (
                            <Button onClick={requestToggle} disabled={!today.rules.checkOutEnabled}><LogOut /> Check out</Button>
                        ) : (
                            <Button onClick={requestToggle} disabled={!today.rules.checkInEnabled}><LogIn /> Check in</Button>
                        )}
                        {(!today.rules.checkInEnabled || !today.rules.checkOutEnabled) && (
                            <p className="text-xs text-muted-foreground">Check-in/out is turned off by HR.</p>
                        )}
                    </div>
                </div>
            </Card>

            <Card>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                    <h2 className="font-display text-card-title text-foreground">Today's sessions</h2>
                    <div className="flex items-center gap-2">
                        {today.pendingAdjustments > 0 && (
                            <Button size="sm" variant="ghost" onClick={onShowAdjustments}>{today.pendingAdjustments} request(s) waiting</Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setAdjust({})}>Forgot to check in or out?</Button>
                    </div>
                </div>
                {today.sessions.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-muted-foreground">No sessions yet today.</p>
                ) : (
                    <ul className="divide-y divide-border">
                        {today.sessions.map((s) => (
                            <li key={s.id} className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 ${s.status === 'SUPERSEDED' ? 'opacity-60' : ''}`}>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium tabular-nums text-foreground">
                                        {fmtTime(s.checkIn)} → {s.status === 'OPEN' ? 'now' : s.checkOut ? fmtTime(s.checkOut) : 'no check-out'}
                                        <span className="ml-2 text-xs font-normal text-muted-foreground">Session {s.label}</span>
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {[s.checkInLocation, s.checkOutLocation && s.checkOutLocation !== s.checkInLocation ? `→ ${s.checkOutLocation}` : null].filter(Boolean).join(' ') || (s.id === 'pending' ? 'Locating…' : 'Location not verified')}
                                        {s.exception ? ` · ${s.exception}` : ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm tabular-nums text-muted-foreground">{s.status === 'OPEN' ? fmtMinutes(openMinutes) : fmtMinutes(s.creditedMinutes)}</span>
                                    <SessionStatusBadge status={s.status} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            <AdjustmentDialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)} preset={adjust || undefined}
                onDone={() => fetchToday().catch(() => undefined)} />
            {confirmDialog}
        </div>
    );
}
